// directive-inbox-mattermost-sync.ts — F06/F07 wiring: composes
// mattermost-poll-cursor-store.ts (cursor+overlap+dedupe) and
// mattermost-command-parser.ts (actor/channel/thread/time filter + 8
// commands) into directive-inbox.ts's writeDirective. Split out of
// directive-inbox.ts to keep that file under the 200-line convention — this
// module is the ONLY thing that turns a raw Mattermost post into an inbox
// directive; directive-inbox.ts itself stays source-agnostic.
//
// Ordering contract inherited from pollChannelSinceCursor: writeDirective is
// called (and awaited) for a post BEFORE the poll cursor persists past it,
// so a crash between "wrote directive" and "advanced cursor" only risks a
// harmless re-delivery (deduped by writeDirective's own derived id), never a
// silent loss (spec §7.3 "lưu post gốc chuẩn hóa vào inbox trước khi tiến
// cursor").
import { writeDirective } from "./directive-inbox.js";
import {
  chatStatusReply,
  enqueueChatCommandReply,
  LEGACY_CHAT_ANSWER_REPLY,
} from "./mattermost-chat-command-reply.js";
import { buildCommandParserContext, parseMattermostCommand } from "./mattermost-command-parser.js";
import { pollChannelSinceCursor } from "./mattermost-poll-cursor-store.js";
import type { MattermostRestClient } from "./mattermost-rest-client.js";

export interface SyncMattermostDirectivesInput {
  client: Pick<MattermostRestClient, "getPostsForChannel">;
  channelId: string;
  controlRoot: string;
  machineLockPath: string;
  missionId: string;
  botUserId: string;
  boundUserId: string;
  expectedRootId: string;
  missionCreatedAtMs: number;
  hintThrottleSec: number;
  perPage?: number;
  now?: () => Date;
}

/**
 * One poll cycle: fetches new channel posts since the persisted cursor,
 * runs each through the command-parser acceptance filter, and writes every
 * ACCEPTED command as a directive (source: "mattermost"). Rejected posts
 * (wrong user/channel/thread/time, bot post, quoted/fenced, unrecognized)
 * are silently skipped here — unknown-command hinting is the caller's job.
 * Two commands that are deliberately NOT directives get a thread reply
 * instead of silence (M1/M2): the legacy `answer <ticket> <req>:` form and
 * `status`. The reply is only RECORDED in the outbox (the transport tick's
 * outbox worker POSTs it) — this module still never calls Mattermost itself.
 * `repliesRecorded` lets the tick drain the outbox again right away.
 */
export async function syncMattermostDirectivesIntoInbox(
  input: SyncMattermostDirectivesInput,
): Promise<{ repliesRecorded: number }> {
  let repliesRecorded = 0;
  const context = buildCommandParserContext({
    botUserId: input.botUserId,
    boundUserId: input.boundUserId,
    expectedChannelId: input.channelId,
    expectedRootId: input.expectedRootId,
    missionCreatedAtMs: input.missionCreatedAtMs,
    hintThrottleSec: input.hintThrottleSec,
  });

  await pollChannelSinceCursor({
    client: input.client,
    channelId: input.channelId,
    controlRoot: input.controlRoot,
    perPage: input.perPage ?? 60,
    // CXR-07: the parser rejects posts before the mission anyway — paging stops there.
    notBeforeMs: input.missionCreatedAtMs,
    now: input.now,
    onNormalizedPost: async (post) => {
      const result = parseMattermostCommand(post, context);
      // L10: count only real inserts — a dedup hit needs no second drain.
      const reply = async (message: string) => {
        const inserted = await enqueueChatCommandReply({
          controlRoot: input.controlRoot,
          missionId: input.missionId,
          channelId: input.channelId,
          rootId: input.expectedRootId,
          sourcePostId: post.id,
          sequence: post.create_at,
          message,
          now: input.now,
        });
        if (inserted) repliesRecorded += 1;
      };
      if (!result.ok) {
        if (result.reason === "legacy_answer_form") await reply(LEGACY_CHAT_ANSWER_REPLY);
        return;
      }
      // "status" is a read-only query (spec §7.2), not one of
      // DirectiveKindSchema's kinds, so it never goes through the inbox. The
      // chat has no status renderer yet (M2) — say so and name the CLI
      // command rather than dropping the post silently.
      if (result.directive.kind === "status") {
        await reply(chatStatusReply(input.missionId));
        return;
      }

      await writeDirective({
        controlRoot: input.controlRoot,
        machineLockPath: input.machineLockPath,
        directive: {
          mission_id: input.missionId,
          kind: result.directive.kind,
          source: "mattermost",
          actor_id: input.boundUserId,
          payload: result.directive.payload,
          source_post_id: post.id,
        },
        now: input.now,
      });
    },
  });
  return { repliesRecorded };
}
